/*@jsxRuntime automatic @jsxImportSource react*/
/*VERIFY (use case, step 2 below): Functionality is confirmed — per Meta's Use Case Permission Mapping (https://developers.facebook.com/documentation/development/create-an-app/use-cases-permission-mapping, updated Aug 29 2025), all three Marketing API use cases require `ads_management` and offer `business_management` as optional, so any of them works for this integration. The only open item is which one the platform team wants as the recommended default.*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Meta Custom Audiences", keywords = "Facebook Custom Audiences, Facebook Ads, ldu, limited data use, fbclid, fbp", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Meta Custom Audiences",
        description: null,
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "facebook",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Meta Custom Audiences",
          "depth": 0,
          "id": "connect-to-meta-custom-audiences"
        }, {
          "value": "Authenticate with a system user token",
          "depth": 1,
          "id": "authenticate-with-a-system-user-token"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Validate your setup",
          "depth": 0,
          "id": "validate-your-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4",
      br: "br",
      img: "img",
      ol: "ol",
      em: "em",
      hr: "hr",
      pre: "pre"
    }, _provideComponents(), props.components), {MatchBoosterDestination, ArrayFanOutTip, MatchRatesExplanation, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Deliver even more relevant ads by combining data from various sources within your data warehouse to build custom audiences for Meta Ads. By keeping your custom audiences updated automatically, never show an ad to someone after they purchase the item you were promoting."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-custom-audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update custom audiences in Meta"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.facebook.com/docs/marketing-api/audiences/guides/custom-audiences",
                children: "Custom Audiences docs"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-meta-custom-audiences",
        children: "Connect to Meta Custom Audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Meta Custom Audiences"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Meta Custom Audiences either with a system user token or with OAuth."]
      }), "\n", _jsx(_components.p, {
        children: "Since the OAuth flow requires you to manually refresh this connection every 60 days, we strongly recommend using a system user token for indefinite access."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure to select an ", _jsx(_components.strong, {
            children: "Ad Account"
          }), " on the destination configuration page.\nOtherwise, your sync fails with an ", _jsx(_components.a, {
            href: "#unsupported-post-request-object-with-id-undefined-does-not-exist",
            children: _jsx(_components.code, {
              children: "'undefined' does not   exist"
            })
          }), "\nerror."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-a-system-user-token",
        children: "Authenticate with a system user token"
      }), "\n", _jsxs(_components.p, {
        children: ["When authenticating with a system user token you can choose to either use an existing one or create a new one for the Hightouch integration.\nTo use an ", _jsx(_components.a, {
          href: "#use-an-existing-system-user-token",
          children: "existing system user token"
        }), ", you must meet the following prerequisites:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The system user whose token you'd like to use has the ", _jsx(_components.code, {
            children: "Employee"
          }), " role. (", _jsx(_components.code, {
            children: "Admin"
          }), " also works, but ", _jsx(_components.a, {
            href: "https://developers.facebook.com/docs/business-management-apis/system-users/overview",
            children: "Meta limits each business to a single Admin system user"
          }), " and recommends using a regular system user to access assets, so ", _jsx(_components.code, {
            children: "Employee"
          }), " is the practical choice.)"]
        }), "\n", _jsxs(_components.li, {
          children: ["You've created an app to add as an assigned asset to the system user. For detailed instructions, see steps 1 - 5 from ", _jsx(_components.a, {
            href: "#create-a-new-system-user-token",
            children: "creating a new system user token"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["You've assigned the ad account that you want to create audiences for to the system user. For detailed instructions, see step 8 from ", _jsx(_components.a, {
            href: "#create-a-new-system-user-token",
            children: "creating a new system user token"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you haven't met these prerequisites, follow the instructions for ", _jsx(_components.a, {
          href: "#create-a-new-system-user-token",
          children: "creating a new system user token"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-new-system-user-token",
        children: "Create a new system user token"
      }), "\n", _jsx(_components.p, {
        children: "To create a new token, you first need to add a new app."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You create the app in the Meta developer portal, then create the system user and generate its token in ", _jsx(_components.strong, {
            children: "Settings"
          }), " at ", _jsx(_components.a, {
            href: "https://business.facebook.com",
            children: "business.facebook.com"
          }), "."]
        })
      }), "\n", "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Meta is replacing its older app-creation workflow with a use-case-based flow. The ", _jsx(_components.strong, {
            children: "legacy workflow"
          }), " is still available as the ", _jsx(_components.strong, {
            children: "Other"
          }), " option (under ", _jsx(_components.strong, {
            children: "Looking for something else?"
          }), ", marked \"going away soon\"), which creates the app in Meta's old experience where you choose permissions manually.", _jsx(_components.br, {}), "\n", _jsx(_components.strong, {
            children: "Existing apps"
          }), " created with ", _jsx(_components.strong, {
            children: "Other"
          }), " keep working — you don't need to recreate them.", _jsx(_components.br, {}), "\n", _jsx(_components.strong, {
            children: "New apps"
          }), " should follow the use-case flow below: selecting any of the three Marketing API use cases (", _jsx(_components.strong, {
            children: "Create & manage ads with Marketing API"
          }), ", ", _jsx(_components.strong, {
            children: "Measure ad performance data with Marketing API"
          }), ", or ", _jsx(_components.strong, {
            children: "Capture & manage ad leads with Marketing API"
          }), ") attaches the ", _jsx(_components.code, {
            children: "ads_management"
          }), " permission this integration needs."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-use-case-other.png",
          alt: "Meta app creation showing the deprecating Other option and the option to create an app without a use case"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Create the app in the ", _jsx(_components.a, {
          href: "https://developers.facebook.com/apps/creation/",
          children: "Meta developer portal"
        }), ". Meta's wizard walks you through five steps: ", _jsx(_components.strong, {
          children: "App details"
        }), ", ", _jsx(_components.strong, {
          children: "Use cases"
        }), ", ", _jsx(_components.strong, {
          children: "Business"
        }), ", ", _jsx(_components.strong, {
          children: "Requirements"
        }), ", and ", _jsx(_components.strong, {
          children: "Overview"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enter a name and contact email."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-details.png",
          alt: "Entering the app name and contact email on the App details step"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select a Marketing API use case such as ", _jsx(_components.strong, {
            children: "Create & manage ads with Marketing API"
          }), ". Per Meta's ", _jsx(_components.a, {
            href: "https://developers.facebook.com/documentation/development/create-an-app#use-case-permission-mapping",
            children: "use case permission mapping"
          }), ", any of the three attaches the ", _jsx(_components.code, {
            children: "ads_management"
          }), " this integration needs and makes ", _jsx(_components.code, {
            children: "business_management"
          }), " available — you select both when generating the token (step 12)."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-use-case.png",
          alt: "Selecting a Marketing API use case during app creation"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Connect the business portfolio that owns the ad account you want to sync to."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-business.png",
          alt: "Connecting a business portfolio to the app"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "Review any listed requirements — typically none when you're accessing your own ad account."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-requirements.png",
          alt: "Reviewing app publishing requirements"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["Review the summary, then select ", _jsx(_components.strong, {
            children: "Create app"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-app-overview.png",
          alt: "Reviewing and creating the app"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Settings"
          }), " at ", _jsx(_components.a, {
            href: "https://business.facebook.com",
            children: "business.facebook.com"
          }), ", go to ", _jsx(_components.strong, {
            children: "Users"
          }), " → ", _jsx(_components.strong, {
            children: "System users"
          }), " and add a new system user with the ", _jsx(_components.strong, {
            children: "Employee"
          }), " role. ", _jsx(_components.a, {
            href: "https://developers.facebook.com/docs/business-management-apis/system-users/overview",
            children: "Meta caps each business at one Admin system user and recommends a regular system user for asset access"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-system-user.png",
          alt: "Creating a system user with the Employee role"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "7",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the system user you just created, then click ", _jsx(_components.strong, {
            children: "Assign assets"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-assign-assets.png",
          alt: "Selecting the system user and clicking Assign assets"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "8",
        children: ["\n", _jsxs(_components.li, {
          children: ["Assign ", _jsx(_components.strong, {
            children: _jsx(_components.em, {
              children: "both"
            })
          }), " the app (under ", _jsx(_components.strong, {
            children: "Apps"
          }), ") and the ad account you want to create audiences for (under ", _jsx(_components.strong, {
            children: "Ad accounts"
          }), "), granting each ", _jsx(_components.strong, {
            children: "Full access"
          }), " — not ", _jsx(_components.strong, {
            children: "Partial access"
          }), " — then click ", _jsx(_components.strong, {
            children: "Assign assets"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-asset-permissions.png",
          alt: "Granting the app and ad account Full access"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "9",
        children: ["\n", _jsxs(_components.li, {
          children: ["Back on the system user, select ", _jsx(_components.strong, {
            children: "Generate token"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-10.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "10",
        children: ["\n", _jsx(_components.li, {
          children: "Choose the app you created to generate the token."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-11.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "11",
        children: ["\n", _jsxs(_components.li, {
          children: ["Set the expiration policy to ", _jsx(_components.strong, {
            children: "Never"
          }), " so the token doesn't lapse and interrupt your syncs. (Meta labels ", _jsx(_components.strong, {
            children: "60 days"
          }), " as recommended, but a system user token is meant for indefinite access.)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-12.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "12",
        children: ["\n", _jsxs(_components.li, {
          children: ["Ensure that both the ", _jsx(_components.code, {
            children: "ads_management"
          }), " and ", _jsx(_components.code, {
            children: "business_management"
          }), " permissions are selected, then click ", _jsx(_components.strong, {
            children: "Generate token"
          }), ". Hightouch uses ", _jsx(_components.code, {
            children: "ads_management"
          }), " to create and update your custom audiences, and ", _jsx(_components.code, {
            children: "business_management"
          }), " to list ad accounts, select existing audiences, and run the test connection."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "You can't change a token's permissions after it's generated. To add a permission later, you need to generate a new token."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-token-permissions.png",
          alt: "Selecting the ads_management and business_management permissions"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "13",
        children: ["\n", _jsx(_components.li, {
          children: "Copy the generated token. Since this token won't be stored in Meta, you may want to consider storing it in a secure password vault as well."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-14.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "14",
        children: ["\n", _jsxs(_components.li, {
          children: ["Back in Hightouch, select ", _jsx(_components.strong, {
            children: "Use system user token"
          }), " as the ", _jsx(_components.strong, {
            children: "Authentication method"
          }), ", then paste the token you generated as the ", _jsx(_components.strong, {
            children: "System User Token"
          }), ", select the relevant ", _jsx(_components.strong, {
            children: "Ads Account"
          }), ", and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-15.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-system-user-token",
        children: "Use an existing system user token"
      }), "\n", _jsxs(_components.p, {
        children: ["If you already have a Meta app and system user setup, in ", _jsx(_components.strong, {
          children: "Settings"
        }), " at ", _jsx(_components.a, {
          href: "https://business.facebook.com",
          children: "business.facebook.com"
        }), ", go to ", _jsx(_components.strong, {
          children: "Users"
        }), " → ", _jsx(_components.strong, {
          children: "System users"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Once you've selected the existing user and assets are assigned, select ", _jsx(_components.strong, {
            children: "Generate token"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-10.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Choose the app you created to generate the token.\n", _jsx(_components.img, {
              src: "destinations/destination-meta-custom-audiences-user-access-token-11.png",
              alt: "Screenshot of the Meta Business account UI"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Set the expiration policy to ", _jsx(_components.strong, {
              children: "Never"
            }), " so the token doesn't lapse and interrupt your syncs. (Meta labels ", _jsx(_components.strong, {
              children: "60 days"
            }), " as recommended, but a system user token is meant for indefinite access.)"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-12.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Ensure that both the ", _jsx(_components.code, {
            children: "ads_management"
          }), " and ", _jsx(_components.code, {
            children: "business_management"
          }), " permissions are selected, then click ", _jsx(_components.strong, {
            children: "Generate token"
          }), ". Hightouch uses ", _jsx(_components.code, {
            children: "ads_management"
          }), " to create and update your custom audiences, and ", _jsx(_components.code, {
            children: "business_management"
          }), " to list ad accounts, select existing audiences, and run the test connection. You can't change a token's permissions after it's generated — to add a permission later, generate a new token."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-token-permissions.png",
          alt: "Selecting the ads_management and business_management permissions"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsx(_components.li, {
          children: "Copy the generated token. Since this token won't be stored in Meta, you may want to consider storing it in a secure password vault as well."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-14.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["Back in Hightouch, select ", _jsx(_components.strong, {
            children: "Use system user token"
          }), " as the ", _jsx(_components.strong, {
            children: "Authentication method"
          }), ", then paste the token you generated as the ", _jsx(_components.strong, {
            children: "System User Token"
          }), ", select the relevant ", _jsx(_components.strong, {
            children: "Ads Account"
          }), ", and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-user-access-token-15.png",
          alt: "Screenshot of the Meta Business account UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Meta"
        }), " and log into your Meta account."]
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Meta Custom Audiences destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Meta Custom Audiences destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you create and maintain custom audiences via the ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/reference/custom-audience/",
          children: "Meta Custom Audience API"
        }), ".\nEach sync adds new users and updates existing users' identifiers you include in ", _jsx(_components.a, {
          href: "#record-matching",
          children: "record matching"
        }), ".\nYou can remove users that leave your model's query result by selecting this option in the sync's ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "delete behavior"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-audience-or-create-a-new-one",
        children: "Select an existing audience or create a new one"
      }), "\n", _jsx(_components.p, {
        children: "You can create a new audience or use an existing one. When creating a new audience, you can optionally enter a name; otherwise, Hightouch defaults to the name of the associated model. To use an existing audience, select the desired audience from the dropdown."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns and the corresponding Meta Custom Audience fields.\nYou can match on default, custom, and Meta-specific fields."
      }), "\n", _jsx(_components.p, {
        children: "Meta-specific fields include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/external-id/",
            children: _jsx(_components.strong, {
              children: "External ID"
            })
          }), ": a custom identifier that you send to Meta via its APIs or tracking pixels"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Page-scoped ID"
          }), ": an ID used for Meta messenger apps and Meta pages"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To increase the match rate for your records, include as many fields as possible."
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Meta Custom Audiences"
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "Meta Custom Audiences"
      }), "\n", _jsx(_components.h4, {
        id: "value-based-audiences",
        children: "Value-based audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're creating a new Meta Audience, you can create a ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/917879191754763?id=401668390442328",
          children: "value-based audience"
        }), " by mapping a numerical model column containing lifetime values to the ", _jsx(_components.strong, {
          children: "Lookalike Value"
        }), " field."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-meta-custom-audiences-lookalike.png",
          alt: "Screenshot of Lookalike Value field mapping"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can then create a lookalike audience in Meta that finds the people most similar to your highest value customers. For more information refer to ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/458132024732845?id=401668390442328",
          children: "Meta's docs"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically hashes the following fields before sending\nrequests to Meta:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Gender"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First Initial"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Zip Code"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Country"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can disable this behavior in the sync configuration. If disabled, the data from the model should be appropriately normalized and hashed according to ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/audiences/guides/custom-audiences/#hash",
          children: "Meta's hashing requirements"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "share-your-audience",
        children: "Share your audience"
      }), "\n", _jsx(_components.p, {
        children: "Optionally share this audience with another Meta advertising account."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You can choose how to handle user records in Meta when the corresponding rows are deleted in your source."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the contact in your Meta Custom Audience"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove from list"
              })
            }), _jsx(_components.td, {
              children: "Remove the contact from your Meta Custom Audience"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "clear-and-fill",
        children: "Clear and fill"
      }), "\n", _jsxs(_components.p, {
        children: ["Where ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#clear-and-fill",
          children: "clear and fill"
        }), " is enabled for your workspace, Hightouch empties and refills the audience inside a single Meta replace session. Meta then applies the new membership in the background, which can take several hours. During that time Meta discards any rows uploaded to the audience, and it reports no error when it does."]
      }), "\n", _jsxs(_components.p, {
        children: ["To protect the refilled audience, Hightouch holds the sync's runs after a clear and fill. Scheduled runs are skipped and manual runs are declined until the hold lifts, and the next run then picks up everything that changed in the meantime. Running another clear and fill during the hold is still allowed, since it replaces the audience again. The default hold is 24 hours. Under ", _jsx(_components.strong, {
          children: "Advanced"
        }), ", you can set ", _jsx(_components.strong, {
          children: "How long should runs wait after a clear and fill?"
        }), " to any value from 5 to 48 hours. A shorter hold risks losing the rows sent before Meta finishes; if you shorten it, compare the audience's matched count the following day before relying on it."]
      }), "\n", _jsx(_components.p, {
        children: "Two Meta limits apply. Meta can't replace an audience with more than 100 million members, so clear and fill fails on those. Clear and fill also isn't available for syncs that match on Page-scoped ID."
      }), "\n", _jsx(_components.h2, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After you configure your sync, run a test to confirm data flows correctly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start with a small test audience or a single record."
        }), "\n", _jsxs(_components.li, {
          children: ["Trigger a manual sync from the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: "Syncs overview"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["After the sync completes, verify the audience in Meta Ads Manager:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Go to ", _jsx(_components.a, {
                href: "https://adsmanager.facebook.com/adsmanager/audiences",
                children: "Audiences"
              }), " and search for the audience name you configured in Hightouch."]
            }), "\n", _jsx(_components.li, {
              children: "Confirm the audience size reflects the expected number of records. Meta may take up to 24 hours to fully process the audience."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "If the sync shows errors, check the sync run log in Hightouch for specific API error messages."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Meta"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Meta returns 1k as the default when there is no data or audience is too\nsmall"
        })
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "Custom Audience Terms not yet accepted"
          })
        }), _jsx(_components.p, {
          children: "The full error message is:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "hljs language-txt",
            children: "An error occurred when creating the audience. Meta Custom Audiences API returned error with code 400: Custom Audience Terms not yet accepted: You'll need to agree to the Custom Audience terms before you can create or edit an audience or an ad set. See Meta, Custom Audience Terms.\n"
          })
        }), _jsxs(_components.p, {
          children: ["To resolve this error, go to ", _jsx(_components.a, {
            href: "https://adsmanager.facebook.com/adsmanager/audiences",
            children: "Audiences"
          }), " in your Meta Ads Manager account and click ", _jsx(_components.strong, {
            children: "Create a Custom Audience"
          }), " to create a list manually. This prompts the Terms of Service to appear so you can agree to them."]
        }), _jsxs(_components.p, {
          children: ["You can also go to ", _jsx(_components.code, {
            children: "https://business.facebook.com/ads/manage/customaudiences/tos/?{ACCOUNT_ID}"
          }), " to accept the Terms of Service for each ad account that you want to use. Replace ", _jsx(_components.code, {
            children: "{ACCOUNT_ID}"
          }), " with the ad account ID in ", _jsx(_components.code, {
            children: "act_xxxx"
          }), " format."]
        }), _jsx(_components.p, {
          children: "Once you've accepted the Terms of Service, rerun your sync with the error."
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "Error Code 400: Missing schema attribute in payloads"
          })
        }), _jsxs(_components.p, {
          children: ["By default, Hightouch automatically ", _jsx(_components.a, {
            href: "#handling-pii-and-hashing",
            children: "hashes certain fields"
          }), " before sending them to Meta.\nThe error occurs when Hightouch attempts to hash a ", _jsx(_components.code, {
            children: "null"
          }), " value or ", _jsx(_components.code, {
            children: "null"
          }), " values are being sent to Meta."]
        }), _jsxs(_components.p, {
          children: ["To resolve this, ", _jsxs(_components.a, {
            href: "/models/sql-editor#exclude-rows-with-null-values",
            children: ["remove rows containing ", _jsx(_components.code, {
              children: "null"
            }), " values"]
          }), " from your model's query results or enable the ", _jsxs(_components.a, {
            href: "/syncs/mapping-data#dont-sync-null-values",
            children: [_jsx(_components.strong, {
              children: "Don't sync null values"
            }), " option"]
          }), " in the ", _jsx(_components.a, {
            href: "/syncs/mapping-data#advanced-mapper",
            children: "advanced mapper"
          }), "."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "Unsupported post request. Object with ID 'undefined' does not exist"
          })
        }), _jsxs(_components.p, {
          children: ["This error occurs if you don't select an ", _jsx(_components.strong, {
            children: "Ad Account"
          }), " on the destination configuration page.\nMake sure to follow the destination setup procedure outlined in the ", _jsx(_components.a, {
            href: "#connect-to-meta-custom-audiences",
            children: "Connect to Meta Custom Audiences"
          }), " section."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "401 - Error validating access token"
          })
        }), _jsxs(_components.p, {
          children: ["As explained in the ", _jsx(_components.a, {
            href: "#connect-to-meta-custom-audiences",
            children: "Connect to Meta Custom Audiences"
          }), " section, authenticating with OAuth requires you to reauthorize your destination ", _jsx(_components.strong, {
            children: "every 60 days"
          }), ". Otherwise, your syncs fail with this error message."]
        }), _jsx(Tip, {
          children: _jsxs(_components.p, {
            children: ["Authenticate with a ", _jsx(_components.a, {
              href: "#authenticate-with-a-system-user-token",
              children: "system user\ntoken"
            }), " to avoid this reauthorization\nrequirement."]
          })
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "400 - OAuthException code 190: Token expired"
          })
        }), _jsxs(_components.p, {
          children: ["The full error may look like: ", _jsx(_components.code, {
            children: "400 - {\"error\":{\"message\":\"The token has expired on...\",\"type\":\"OAuthException\",\"code\":190}}"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["Meta access tokens expire periodically. Tokens also become invalid when the authorizing user changes their password or when Meta invalidates the session for security reasons. In the latter case, the error includes ", _jsx(_components.code, {
            children: "error_subcode: 460"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["To resolve this error, go to your Meta Custom Audiences destination in Hightouch and click ", _jsx(_components.strong, {
            children: "Reconnect"
          }), ". The authorizing user must have ", _jsx(_components.code, {
            children: "ads_management"
          }), " permission on the ad account. To avoid recurring token expiration issues, authenticate with a ", _jsx(_components.a, {
            href: "#authenticate-with-a-system-user-token",
            children: "system user token"
          }), " instead of OAuth."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "403 - Ad account permissions error (code 200)"
          })
        }), _jsxs(_components.p, {
          children: ["The full error may look like: ", _jsx(_components.code, {
            children: "(#200) Ad account owner has NOT grant ads_management or ads_read permission"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["This error occurs when the user who authorized the Meta connection doesn't have the required permissions on the ad account. To resolve it, in ", _jsx(_components.strong, {
            children: "Settings"
          }), " at ", _jsx(_components.a, {
            href: "https://business.facebook.com",
            children: "business.facebook.com"
          }), ", go to ", _jsx(_components.strong, {
            children: "Users"
          }), " → ", _jsx(_components.strong, {
            children: "System Users"
          }), " and grant the user ", _jsx(_components.code, {
            children: "ads_management"
          }), " or ", _jsx(_components.code, {
            children: "ads_read"
          }), " permission on the relevant ad account. Then reconnect the destination in Hightouch."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "400 - (#100) Requires business_management permission"
          })
        }), _jsxs(_components.p, {
          children: ["The full error may look like: ", _jsx(_components.code, {
            children: "400 - {\"error\":{\"message\":\"(#100) Requires business_management permission to access the field.\",\"type\":\"OAuthException\",\"code\":100}}"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["This error occurs when your access token is missing the ", _jsx(_components.code, {
            children: "business_management"
          }), " permission. Hightouch needs this permission to list ad accounts, select existing audiences, and run the test connection."]
        }), _jsxs(_components.p, {
          children: ["If you authenticated with a ", _jsx(_components.a, {
            href: "#authenticate-with-a-system-user-token",
            children: "system user token"
          }), ", regenerate the token in ", _jsx(_components.strong, {
            children: "Settings"
          }), " → ", _jsx(_components.strong, {
            children: "Users"
          }), " → ", _jsx(_components.strong, {
            children: "System Users"
          }), " at ", _jsx(_components.a, {
            href: "https://business.facebook.com",
            children: "business.facebook.com"
          }), " with both ", _jsx(_components.code, {
            children: "ads_management"
          }), " and ", _jsx(_components.code, {
            children: "business_management"
          }), " selected, then reconnect the destination in Hightouch."]
        }), _jsxs(_components.p, {
          children: ["If you authenticated with OAuth, click ", _jsx(_components.strong, {
            children: "Reconnect"
          }), " and approve all requested permissions when prompted."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "400 - (#100) unsupported get request"
          })
        }), _jsxs(_components.p, {
          children: ["The full error may look like: ", _jsx(_components.code, {
            children: "400 - {\"error\":(\"message\":\"(#100) Unsupported get request. Please read the Graph API documentation at https://developers.facebook.com/docs/graph-api {...}"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["This error usually happens when one or multiple Meta pages that were granted access to the ", _jsx(_components.a, {
            href: "#authenticate-with-a-system-user-token",
            children: "system user"
          }), " have ", _jsx(_components.strong, {
            children: "age restrictions"
          }), " or ", _jsx(_components.strong, {
            children: "country restrictions"
          }), " enabled. To resolve the error, edit your system user to remove all pages that have these age or country restrictions. Otherwise, you can ", _jsx(_components.a, {
            href: "https://www.facebook.com/help/www/778445532225441",
            children: "edit the page settings"
          }), " to remove the restrictions from these pages."]
        })]
      }), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "Clear and fill"
          })
        }), _jsxs(_components.p, {
          children: ["Upon request, ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#clear-and-fill",
            children: "clear and fill"
          }), " can be enabled for Meta. After a clear and fill, the sync's runs are held while Meta applies the new membership. See ", _jsx(_components.a, {
            href: "#clear-and-fill",
            children: "Clear and fill"
          }), " for how the hold works and how to adjust it."]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
